import type { KYDAudienceData } from "@common/interfaces/KYDMarketing";
import type { CreateCustomAudienceFormValues } from "./types";

/** Display/save name: manual field, else selected segment label. */
export function resolveKydAudienceName(
  form: CreateCustomAudienceFormValues
): string {
  const manual = form.name?.trim();
  if (manual) return manual;
  return form.segment?.label?.trim() ?? "";
}

export function canProceedKydAudienceFormStep1(
  form: CreateCustomAudienceFormValues
): boolean {
  const hasName = Boolean(resolveKydAudienceName(form));
  const hasSegment = Boolean(form.segment?.value);
  const hasGenres = (form.genres?.length ?? 0) > 0;
  const hasArtists = (form.artists?.length ?? 0) > 0;
  return hasName && (hasSegment || hasGenres || hasArtists);
}

function mapOptionsToStrings(
  options: CreateCustomAudienceFormValues["genres"] | undefined
): string[] {
  return (options ?? []).map((o) => o.value).filter(Boolean);
}

export function kydAudienceDraftSize(
  data: CreateCustomAudienceFormValues
): number | undefined {
  if (data.segment_size != null && Number.isFinite(data.segment_size)) {
    return data.segment_size;
  }
  return undefined;
}

export function formValuesToKydAudienceData(
  data: CreateCustomAudienceFormValues
): KYDAudienceData & { approximate_count_upper_bound?: number } {
  const segmentId = data.segment?.value;
  const draftSize = kydAudienceDraftSize(data);

  const segmentSelected = Boolean(segmentId);
  return {
    name: resolveKydAudienceName(data),
    audience_type: "kyd",
    ...(segmentId ? { segment_id: segmentId } : {}),
    genres: segmentSelected ? [] : mapOptionsToStrings(data.genres),
    artists: segmentSelected ? [] : mapOptionsToStrings(data.artists),
    ...(data.lookalike?.name && data.lookalike?.ratio
      ? {
          lookalike: {
            name: data.lookalike.name,
            ratio: data.lookalike.ratio,
          },
        }
      : {}),
    ...(segmentId && draftSize != null
      ? { approximate_count_upper_bound: draftSize }
      : {}),
  };
}
